import React from 'react';
import styled from 'styled-components';

import { Button, Icon, Tooltip } from '@column/column-ui-kit';

import { type TimelineEntry, TimelineStatus } from '~/app/pages/Transfers/_components/Timeline/types';
import { buildTimelineEntryForTransferReviews, buildTransferParty } from '~/app/pages/Transfers/_utils/timeline';
import { TransferStatus } from '~/lib/transfers';
import { formatInternationalWireExchangeRate } from '~/lib/transfers/intlWire';
import { getReturnReasonDisplay, swiftReturnReasons } from '~/lib/transfers/returnReasons';
import type { Counterparty, IntlWireTransfer } from '~/repositories';
import type { AccountNumber, BankAccount } from '~/repositories/BankAccountRepository';
import type { FxQuote } from '~/typings/API';
import { formatDateTime } from '~/util';

interface QuoteDetailsParams {
  context: 'copy-row' | 'details-card';
  isSandbox: boolean;
  isLoading: boolean;
  onRetry?: () => void;
  quote?: FxQuote;
}

const WarningIcon = styled(Icon.Warning)`
  --icon-color: ${({ theme }) => theme.warning.background};
  --icon-size: 16px;
`;

const RateValue = styled.div<{ $context: QuoteDetailsParams['context'] }>`
  display: flex;
  align-items: center;
  gap: 4px;
  min-height: 16px;
  color: ${({ $context, theme }) =>
    $context === 'details-card' ? theme.foreground : theme.secondary.blendToBackground(800)};
  font-size: ${({ $context, theme }) => ($context === 'details-card' ? '14px' : theme.style.inputSmallFontSize)};
  font-weight: ${({ $context, theme }) => ($context === 'details-card' ? 500 : theme.style.inputSmallFontWeight)};
  line-height: ${({ $context, theme }) => ($context === 'details-card' ? '16px' : theme.style.inputSmallLineHeight)};
`;

export const getInternationalWireReviewFXQuoteDetails = ({
  context,
  isLoading,
  isSandbox,
  onRetry,
  quote,
}: QuoteDetailsParams) => [
  {
    label: 'Foreign Exchange Rate',
    value: quote ? (
      <RateValue $context={context}>
        <span>{formatInternationalWireExchangeRate(quote)}</span>
        {isSandbox && (
          <Tooltip content="In Sandbox Mode, foreign exchange rates may not reflect market rates." zIndex={15}>
            <WarningIcon />
          </Tooltip>
        )}
      </RateValue>
    ) : isLoading ? (
      'Loading...'
    ) : onRetry ? (
      <RateValue $context={context}>
        <Button size="small" variant="secondary" onClick={onRetry}>
          Retry Quote
        </Button>
      </RateValue>
    ) : (
      '-'
    ),
  },
  {
    label: 'Rate Valid Until',
    value: quote ? formatDateTime(quote.expiredAt, { timeZoneFormat: 'local' }) : isLoading ? 'Loading...' : '-',
  },
];

const internationalWireEvents = [
  {
    label: 'Created',
    field: 'createdAt',
  },
  {
    label: 'Pending Approval',
    field: 'preReviewAt',
  },
  {
    label: 'Initiated',
    field: 'initiatedAt',
  },
  {
    label: 'Pending Submission',
    field: 'pendingSubmissionAt',
    status: TimelineStatus.Pending,
  },
  {
    label: 'Submitted',
    field: 'submittedAt',
  },
  {
    label: 'Completed',
    field: 'completedAt',
    status: TimelineStatus.Success,
  },
  {
    label: 'Rejected',
    field: 'rejectedAt',
    status: TimelineStatus.Danger,
  },
];

export const buildInternationalWireEvents = ({
  transfer,
  account,
  counterparty,
}: {
  transfer: IntlWireTransfer;
  account: {
    accountNumber: AccountNumber;
    bankAccount: BankAccount;
    entityType?: 'person' | 'business';
  };
  counterparty: Counterparty;
}): TimelineEntry[] => {
  const rejected = (transfer.status?.toLowerCase() as TransferStatus) === TransferStatus.REJECTED;
  const events = internationalWireEvents
    .filter(
      (event) =>
        event.field === 'createdAt' ||
        (!rejected && event.field === 'completedAt') ||
        !!transfer[event.field as keyof typeof transfer]
    )
    .map((event) => {
      if (event.field === 'preReviewAt') {
        return buildTimelineEntryForTransferReviews(
          transfer[event.field as keyof typeof transfer] as string | undefined,
          transfer.reviews
        );
      }

      let amount;
      if (transfer.amount) {
        amount = {
          value: typeof transfer.amount === 'string' ? parseFloat(transfer.amount) : transfer.amount,
          isAdding: event.field === 'completedAt',
          currencyCode: transfer.currencyCode ?? 'USD',
          settledAmount: transfer.settledAmount ?? undefined,
          settledCurrencyCode: transfer.settledCurrencyCode,
          instructedCurrencyCode: transfer.instructedCurrencyCode,
          instructedAmount: transfer.instructedAmount,
          isIncoming: transfer.isIncoming,
        };
      }

      const date = (transfer[event.field as keyof typeof transfer] as string | undefined)
        ? new Date(transfer[event.field as keyof typeof transfer] as string)
        : undefined;

      const partyData =
        event.field === 'createdAt' || event.field === 'completedAt'
          ? buildTransferParty({
              account,
              counterparty,
              isIncoming: transfer.isIncoming ?? false,
              isOriginator: event.field === 'createdAt',
            })
          : undefined;

      const defaultStatus = rejected ? TimelineStatus.Danger : TimelineStatus.Pending;
      const status =
        event.field === 'pendingSubmissionAt' && transfer.pendingSubmissionAt && transfer.submittedAt
          ? TimelineStatus.Success
          : !!transfer[event.field as keyof typeof transfer]
            ? event.status
            : defaultStatus;

      return {
        label: event.label,
        amount,
        date: date,
        partyData,
        status,
      };
    });

  if (rejected) {
    events.push({
      label: 'Rejected',
      amount: transfer.amount
        ? {
            value: typeof transfer.amount === 'string' ? parseFloat(transfer.amount) : transfer.amount,
            isAdding: false,
            currencyCode: transfer.currencyCode ?? 'USD',
            settledAmount: transfer.settledAmount ?? undefined,
            settledCurrencyCode: transfer.settledCurrencyCode,
            instructedCurrencyCode: transfer.instructedCurrencyCode,
            instructedAmount: transfer.instructedAmount,
            isIncoming: transfer.isIncoming,
          }
        : undefined,
      status: TimelineStatus.Danger,
      partyData: buildTransferParty({
        account,
        counterparty,
        isIncoming: transfer.isIncoming ?? false,
        isOriginator: false,
      }),
      date: transfer.updatedAt ? new Date(transfer.updatedAt) : undefined,
    });
  }

  // A return is pending until the originator bank confirms it; only then is returnedAt set.
  const isPendingReturn = transfer.status === 'pending_return' && !transfer.returnedAt;
  if (transfer.returnedAt || isPendingReturn) {
    const returnedAmount =
      typeof transfer.returnedAmount === 'string' ? parseFloat(transfer.returnedAmount) : transfer.returnedAmount;
    const returnedCurrencyCode = transfer.returnedCurrencyCode ?? transfer.currencyCode ?? 'USD';
    // The API has no pending-return timestamp, so the last update stands in while the return is in flight.
    const returnDate = isPendingReturn ? transfer.updatedAt : transfer.returnedAt;
    const reason = transfer.returnReason
      ? getReturnReasonDisplay(swiftReturnReasons, transfer.returnReason)
      : undefined;

    events.push({
      label: isPendingReturn ? 'Return Initiated' : 'Returned',
      amount: returnedAmount
        ? {
            value: returnedAmount,
            isAdding: false,
            currencyCode: returnedCurrencyCode,
          }
        : undefined,
      status: TimelineStatus.Returned,
      date: returnDate ? new Date(returnDate) : undefined,
      internationalWireReturnData: reason
        ? {
            reason: reason.code,
            reasonDescription: reason.description,
            amount: returnedAmount ?? 0,
            currencyCode: returnedCurrencyCode,
          }
        : undefined,
      returnData: undefined,
    });
  }

  return events;
};
